import RhSolicitudesVacaciones from "./../db/RhSolicitudesVacaciones.js";

/** Se ha cerrado la sesión: lo que hay en pantalla es de otra persona. */
export const MOTIVO_CIERRE_DE_SESION = 'cierre-de-sesion';

/** Se sale de la pantalla (Atrás, el botón físico o una navegación del programa). */
export const MOTIVO_SALIDA_DE_PANTALLA = 'salida-de-pantalla';

/** La pantalla se destruye de verdad. */
export const MOTIVO_DESTRUCCION = 'destruccion';

/**
 * La **ventana** se cierra, o pasa a ser la de otra solicitud. La pantalla sigue delante y la
 * sesión es la misma: lo que ha cambiado es de quién es lo que se está mirando (#69).
 */
export const MOTIVO_OTRA_VENTANA = 'otra-ventana';

/**
 * **EL ÚNICO SITIO QUE ENUMERA CÓMO UNA PANTALLA DE ESTE MÓDULO DEJA DE SER LA DE DELANTE** (#58).
 *
 * Las rutas del módulo son `keepAlive`: la pantalla **no se destruye** al salir de ella ni al
 * cerrar la sesión. Así que lo que tuviera pedido sigue siendo «la petición vigente» de su carril
 * (`carrilPeticiones`) mucho después de que nadie la mire, y su respuesta —o su error— todavía
 * puede pintar datos que ya no son de quien está delante, abrir un aviso **encima de otra
 * pantalla** o apagar el «cargando», que es un modal de la **aplicación** y no de la pantalla que
 * lo encendió.
 *
 * ## Por qué está en un fichero y no en un hook en cada pantalla
 *
 * Es una sola familia de fallos —«la pantalla deja de estar delante y lo que pidió sigue
 * decidiendo»— y salió **tres rondas seguidas** de la misma revisión, una por cada forma de dejar
 * de estar delante, porque cada vez se tapó solo la que venía señalada:
 *
 *  - **al destruirse** el formulario con el envío en vuelo (PR #1733, ronda 6);
 *  - **al cerrar sesión**, que con `keepAlive` no destruye nada y no se notaba hasta la siguiente
 *    entrada en la ruta (ronda 7);
 *  - **al salir de la pantalla** con Atrás, con el botón físico o por una navegación del programa,
 *    que tampoco destruye nada (ronda 8).
 *
 * Mientras las salidas vivan repartidas en los hooks de cada pantalla, la siguiente pantalla del
 * módulo (#59, #61) nace cubriendo dos de tres y la ronda siguiente trae la que falte. Aquí están
 * las tres juntas: cualquiera de ellas acaba llamando al **mismo** método de la pantalla, así que
 * añadir una cuarta —si algún día la hay— es una línea en un sitio.
 *
 * Y la hay, que es la **cuarta** y la trajo la #69: una **ventana** que se cierra o que pasa a ser
 * la de otra persona, con la pantalla delante y la sesión intacta
 * ({@link MOTIVO_OTRA_VENTANA}, {@link soloMandaLaVentanaDeAhora}).
 *
 * ## Cómo se usa
 *
 * La pantalla implementa `dejarDeMandar(motivo)`: tirar sus carriles, soltar sus relojes y abrir
 * los cerrojos que ya no va a soltar nadie. Y añade a su `f7-page` la salida, que es el único de
 * los tres avisos que no se puede dar desde aquí —los eventos de página los emite el `f7-page`, que
 * es de la pantalla—, en la misma línea en la que ya tenía la entrada:
 *
 *     <f7-page @page:beforein="entrar" @page:beforeout="salirDeLaPantalla">
 *
 * El `motivo` llega porque las tres salidas **no piden lo mismo**: al cerrar sesión hay que tirar
 * además lo que se está viendo —son datos personales de otra persona—, y al salir no, que la
 * pantalla se queda como estaba y la siguiente entrada la recarga. Lo que sí es igual en las tres
 * es que nada de lo que esté en vuelo vuelve a decidir.
 *
 * `dejarDeMandar` tiene que poder llamarse **dos veces seguidas** sin estropear nada: salir de la
 * pantalla y destruirla son dos avisos distintos del mismo viaje.
 *
 * Un popup que viva dentro de una pantalla (el formulario de solicitud, la ventana de registrar en
 * nombre de otra persona) no recibe el aviso de salida por su cuenta: la página es la que sabe que
 * se va, y se lo pasa a sus hijos con {@link avisar}. Los otros dos sí los tiene, porque el cierre
 * de sesión es del módulo y la destrucción es suya — siempre que el popup **también** use este
 * mixin, que es lo que lo apunta a los tres avisos.
 *
 * Y la pantalla que contenga varios enumera los suyos **en un solo sitio**, no uno por hook: el
 * segundo popup de «Mis vacaciones» nació sin ninguno de los tres avisos y se quedaba abierto
 * sobre el login con una respuesta en vuelo, que es la misma familia de fallos contada una cuarta
 * vez (revisión CODEX del PR #1737).
 *
 * La pieza común de los carriles (`carrilPeticiones`) **no se toca**: es la decisión de dirección
 * del 09/10/2026 en la #58 —que se encargue ella por las once pantallas que la usan va en una
 * ficha aparte—. Esto es del módulo de vacaciones y de sus pantallas.
 *
 * ## El sello, para lo que no es un carril
 *
 * Una petición la descarta su carril, pero **una acción no**: aprobar o denegar no es una lectura
 * que se pueda tirar, y lo que hay que descartar son sus EFECTOS sobre la pantalla —cerrar el
 * detalle, recargar la rejilla, el acuse—. Para eso está {@link selloDeLaPantalla}: las mismas
 * tres salidas que llaman a `dejarDeMandar` suben un contador, y quien tomó el sello antes
 * compara y se calla. Es el mismo trato que `accionDeLaSesion` le da a la sesión, con la otra
 * pregunta: aquélla es «¿sigue siendo la misma persona?» y ésta «¿sigue siendo la misma pantalla?».
 *
 * ## Y lo que no se puede descartar al volver, porque no vuelve
 *
 * Preguntar al llegar la respuesta solo sirve si la respuesta llega. Lo que está puesto MIENTRAS
 * —el indicador de espera, que es un modal de la aplicación— se queda tapando la pantalla de
 * destino si la petición se cuelga. Eso se apunta con {@link alDejarDeMandar} y se suelta en las
 * mismas tres salidas, sin esperar a nadie.
 */
/**
 * El aviso, con el contrato comprobado. Una pantalla que use esto sin implementar `dejarDeMandar`
 * lo dice en la consola en vez de reventar: esto corre dentro del aviso del cierre —donde una
 * excepción dejaría sin enterarse a las pantallas que vayan detrás— y dentro de `beforeDestroy`.
 *
 * Se exporta para que una pantalla pase el aviso de salida a sus popups con el **mismo** contrato
 * comprobado: un hijo que no lo implemente lo dice en la consola, en vez de reventar el
 * `page:beforeout` y dejar a la propia pantalla sin tirar lo suyo.
 */
export function avisar(pantalla, motivo) {
  // El sello sube SIEMPRE, también cuando la pantalla no implementa `dejarDeMandar`: que le falte
  // el método no la devuelve a estar delante.
  pantalla.$_generacionDeLaPantalla = (pantalla.$_generacionDeLaPantalla || 0) + 1;
  // Y lo que tenga apuntado para soltar se suelta también SIEMPRE, antes que `dejarDeMandar` y
  // aunque la pantalla no lo implemente: son testigos de cosas globales —el indicador de espera—
  // que no son de nadie de la pantalla y que, si no se sueltan aquí, no los suelta ya nadie.
  soltarLoApuntado(pantalla, motivo);
  if (typeof pantalla.dejarDeMandar !== 'function') {
    console.error('[RH-VAC] Esta pantalla usa `soloMandaLaDeDelante` pero no implementa '
      + '`dejarDeMandar(motivo)`, así que lo que tenga en vuelo sigue mandando (' + motivo + ').');
    return;
  }
  pantalla.dejarDeMandar(motivo);
}

/**
 * **«¿Sigue delante la pantalla desde la que se empezó esto?»**, preguntado más tarde.
 *
 * Se toma al empezar la acción y se pregunta al contestar el servidor. Devuelve `false` en cuanto
 * la pantalla ha pasado por **cualquiera** de las tres salidas: no hace falta que se destruya —con
 * `keepAlive` no se destruye— ni que cambie la cuenta, que es justo el hueco que dejaba el sello de
 * sesión. Una resolución confirmada en el calendario y una navegación a otra pantalla antes de que
 * contestara el POST recargaba igual la rejilla: el preloader global encendido y varias consultas
 * lanzadas desde una pantalla que ya nadie mira, encima de la de destino (revisión CODEX del PR
 * #1735, 17/10/2026).
 *
 * Volver a entrar **no** rehabilita lo de antes, y es a propósito: la entrada recarga por su cuenta,
 * así que la recarga vieja sobra y su acuse llegaría descolgado de lo que se está viendo.
 *
 * Se le pasa el componente que lanza la acción, que puede ser un hijo de la pantalla —la bandeja
 * vive dentro de «Vacaciones»—: se sube por el árbol hasta la que usa este mixin. Buscarlo aquí y
 * no en cada llamante es lo mismo que se hizo con las salidas; un `inject` en cada componente es
 * otra lista que el siguiente se olvida de poner.
 *
 * Sin pantalla con sello —un componente suelto, un banco de pruebas que monta los métodos a
 * pelo— contesta que sí: esto descarta efectos, y descartarlos por no encontrar el sello sería
 * peor que no tenerlo.
 */
export function selloDeLaPantalla(vm) {
  return selloDe(laPantallaQueLoContiene(vm));
}

/**
 * **«¿Sigue siendo de esto la ventana desde la que se empezó?»**, que es la misma pregunta una
 * vuelta más abajo (#69).
 *
 * Una ventana del módulo —«Pedir anulación», «Anular días»— es **una sola instancia** que la
 * pantalla reutiliza para una solicitud y luego para otra: vive en una ruta `keepAlive` y se abre
 * cambiándole la solicitud. Cerrarla y abrirla sobre otra persona no es ninguna de las tres
 * salidas de arriba —la pantalla sigue delante y la sesión es la misma—, así que el sello de
 * pantalla seguía diciendo que sí y los efectos de la acción de ANTES caían sobre la ventana de
 * AHORA: el acuse nombraba a la segunda persona con los días anulados de la primera y el evento de
 * «ya está» le cerraba su formulario (revisión CODEX del PR #1748, ronda 9).
 *
 * Lo que se descarta con esto es lo que es **de la ventana**: su acuse, su diálogo de error, su
 * propio cierre y la lista que haya pedido para sus desplegables. Lo que es de la pantalla —recargar
 * la rejilla— se descarta con el sello de arriba, y lo de fuera —el contador del menú— con el de
 * sesión y nada más. Son tres preguntas y no una, y cada efecto responde a la suya
 * (`accionDeLaSesion`).
 *
 * Quien no sea una ventana con sello propio obtiene el de su pantalla: para la bandeja, que no es
 * una ventana, las dos preguntas son la misma y se comporta exactamente como antes.
 */
export function selloDeLaVentana(vm) {
  return selloDe(conSelloMasCercano(vm));
}

/** El sello de un componente, o «que sí» si no hay ninguno con sello (ver {@link selloDeLaPantalla}). */
function selloDe(quien) {
  if (!quien) return function() { return true; };
  const generacion = quien.$_generacionDeLaPantalla;
  return function() { return quien.$_generacionDeLaPantalla === generacion; };
}

/**
 * **El componente con sello más cercano**, subiendo por el árbol: la propia ventana si es una de
 * las que llevan el mixin, y si no la pantalla que la contiene.
 */
function conSelloMasCercano(vm) {
  for (let actual = vm; actual; actual = actual.$parent) {
    if (typeof actual.$_generacionDeLaPantalla === 'number') return actual;
  }
  return null;
}

/**
 * **La pantalla del módulo a la que pertenece un componente**, que es el sello de más arriba: una
 * ventana vive dentro de una pantalla y las dos llevan el mixin, así que el de más arriba es
 * siempre el de la pantalla. La bandeja de aprobación vive dentro de «Vacaciones», así que lo que
 * ella lanza es de esa pantalla aunque ella no use el mixin.
 */
function laPantallaQueLoContiene(vm) {
  let pantalla = null;
  for (let actual = vm; actual; actual = actual.$parent) {
    if (typeof actual.$_generacionDeLaPantalla === 'number') pantalla = actual;
  }
  return pantalla;
}

/**
 * **«Suelta esto cuando la pantalla deje de estar delante»**, que es la otra mitad del sello.
 *
 * El sello sirve para **descartar efectos** cuando la respuesta llega: quien lo tomó pregunta y se
 * calla. Pero hay cosas que no esperan a ninguna respuesta porque **puede no llegar nunca** —una
 * petición que se queda colgada— y que mientras tanto siguen puestas sobre la pantalla de destino:
 * el indicador de espera, que es un modal de la APLICACIÓN. Para ésas no vale preguntar al volver;
 * hay que soltarlas al salir, y por eso se apuntan aquí.
 *
 * Es el mismo reparto que ya hace `preloaderDeLaSesion` con el cierre de sesión, con la salida que
 * le faltaba: las tres salidas están enumeradas en este fichero y en ninguno más, así que quien
 * tenga algo que soltar lo apunta y no vuelve a escribir la lista (decisión técnica de Donchurru
 * del 17/10/2026 en la #59: «relevarla, salir o cambiar de sesión libera ese testigo y solo ése»).
 *
 * Devuelve la baja, para llamarla cuando se suelte por el camino normal: lo que ya no está en
 * vuelo no tiene nada que soltar, y dejarlo apuntado haría crecer la lista con cada acción.
 *
 * Lo apuntado se suelta **una vez**: la lista se vacía antes de recorrerla, así que salir de la
 * pantalla y destruirla después —que son dos avisos del mismo viaje— no sueltan dos veces. Y un
 * componente sin pantalla con sello —un banco que monta los métodos a pelo— no apunta nada: su
 * testigo lo suelta quien lo cogió, como antes de esto.
 *
 * Se apunta en **el sello más cercano**, que para una ventana es ella misma: lo que cogió esperando
 * algo suyo lo suelta también cuando la ventana se cierra o pasa a ser de otra persona, que es una
 * salida más de las que esa espera no puede sobrevivir.
 */
export function alDejarDeMandar(vm, soltar) {
  return apuntarEn(conSelloMasCercano(vm), soltar);
}

/**
 * **Lo mismo, apuntado en la PANTALLA** y no en la ventana más cercana, para lo que tiene que
 * soltarse con cualquiera de los dos sellos (#271).
 *
 * Una ventana no recibe las salidas de su pantalla por su cuenta: se las pasa la pantalla con
 * {@link avisar}, y eso es código de cada pantalla. Lo que se suelte por los dos sellos —la pregunta
 * de una acción, que `accionDeLaSesion` da por cancelada si deja de estar delante cualquiera de los
 * dos— se apunta en los dos, y así no depende de que la pantalla se acuerde de avisar a sus hijos.
 * Si la ventana y la pantalla son el mismo componente, se apunta dos veces y se suelta dos: quien
 * apunte aquí tiene que poder soltarse dos veces, como `dejarDeMandar`.
 */
export function alDejarDeMandarLaPantalla(vm, soltar) {
  return apuntarEn(laPantallaQueLoContiene(vm), soltar);
}

function apuntarEn(pantalla, soltar) {
  if (!pantalla) return function() {};
  const apuntados = pantalla.$_sueltaAlDejarDeMandar || (pantalla.$_sueltaAlDejarDeMandar = []);
  apuntados.push(soltar);
  return function() {
    const donde = apuntados.indexOf(soltar);
    if (donde !== -1) apuntados.splice(donde, 1);
  };
}

/** Lo apuntado en {@link alDejarDeMandar}, soltado y quitado de la lista. */
function soltarLoApuntado(pantalla, motivo) {
  const apuntados = pantalla.$_sueltaAlDejarDeMandar;
  if (!apuntados || !apuntados.length) return;
  pantalla.$_sueltaAlDejarDeMandar = [];
  // Sobre una copia: soltar una espera da de baja su apunte, y esa baja borra de ESTA lista. Al
  // recorrerla directamente, cada baja corría al siguiente una posición y el recorrido se lo
  // saltaba: con la espera y el alta del calendario apuntadas, el alta se quedaba cogida
  // (revisión CODEX del PR #1842, ronda 2 del 21/10).
  apuntados.slice().forEach(function(soltar) {
    try {
      soltar(motivo);
    } catch (e) {
      // Lo que le falle a uno no puede dejar al resto sin soltarse, ni cortar el aviso de salida.
      console.error('[RH-VAC] Fallo al soltar lo que esperaba una pantalla que deja de mandar', e);
    }
  });
}

/**
 * **La cuarta salida, para las ventanas: dejar de ser la de esta solicitud** (#69).
 *
 * Lo pone una ventana del módulo —un popup que se abre sobre una solicitud y se reutiliza para la
 * siguiente— **además** del mixin de abajo, y con eso sus acciones y sus lecturas en vuelo quedan
 * atadas a la solicitud con la que se abrió: si se cierra o se abre sobre otra persona, lo que
 * llegue después no pinta nada suyo encima.
 *
 * Va aquí y no como un `watch` en cada ventana por lo mismo que las otras tres: son dos ventanas
 * hoy y las que traiga la #61, y una guarda repartida se olvida en la siguiente que se escriba.
 * La ventana solo dice **de qué es** —el `computed` `sujetoDeLaVentana`, que suele ser el id de la
 * solicitud—; de cuándo eso cambia responde esto.
 */
export const soloMandaLaVentanaDeAhora = {
  created: function() {
    if (typeof this.sujetoDeLaVentana === 'undefined') {
      console.error('[RH-VAC] Esta ventana usa `soloMandaLaVentanaDeAhora` pero no declara el '
        + '`computed` `sujetoDeLaVentana`, así que reabrirla sobre otra solicitud no descartaría '
        + 'lo que la anterior dejó en vuelo.');
    }
  },
  watch: {
    // Las dos cosas cambian al cerrarla —«Mis vacaciones» la cierra poniendo su solicitud a
    // `null`—, así que avisa dos veces, y eso es correcto: el aviso se puede repetir (ver `avisar`).
    opened: function(abierta) {
      if (!abierta) avisar(this, MOTIVO_OTRA_VENTANA);
    },
    sujetoDeLaVentana: function(ahora, antes) {
      if (ahora !== antes) avisar(this, MOTIVO_OTRA_VENTANA);
    }
  }
};

export default {
  created: function() {
    const self = this;
    // El sello arranca aquí y no en la primera salida: sin él, `selloDeLaPantalla` no reconocería
    // a esta pantalla y daría por buenos los efectos de una acción empezada antes de salir.
    this.$_generacionDeLaPantalla = 0;
    // De cuándo se cierra la sesión responde el adaptador, que es el único sitio del módulo que lo
    // sabe y que avisa DESPUÉS de subir su sello (`alCerrarSesion`).
    this.$_avisoDeCierreDeSesion = RhSolicitudesVacaciones.alCerrarSesion(function() {
      avisar(self, MOTIVO_CIERRE_DE_SESION);
    });
  },
  beforeDestroy: function() {
    // Darse de baja primero: si no, el aviso seguiría llamando a una pantalla que ya no existe
    // cada vez que alguien cerrara su sesión.
    this.$_avisoDeCierreDeSesion();
    avisar(this, MOTIVO_DESTRUCCION);
  },
  methods: {
    /** Para el `@page:beforeout` de la plantilla. Ignora el argumento que trae Framework7. */
    salirDeLaPantalla: function() {
      avisar(this, MOTIVO_SALIDA_DE_PANTALLA);
    }
  }
};
